import * as React from "react";
import { Page, Grid, Card, StatsCard, Alert, Text } from "tabler-react";
import TradesSiteWrapper from "./TradesSiteWrapper.react";
import RefreshButton from "./RefreshButton.react";
import {
  getHealth,
  getKpi,
  getTokenStatus,
  getStrategyFleet,
  fmtCurrency,
  fmtDateTime,
  pnlClass,
} from "./api";

class DashboardPage extends React.Component {
  state = { loading: true, error: "", health: null, kpi: null, token: null, fleetRows: [] };

  componentDidMount() {
    this.load();
  }

  load = async () => {
    this.setState({ loading: true, error: "" });
    try {
      const [health, kpi, token, fleet] = await Promise.all([
        getHealth().catch(() => null),
        getKpi().catch(() => null),
        getTokenStatus(),
        getStrategyFleet().catch(() => null),
      ]);
      const fleetRows = (fleet && (fleet.strategies || fleet.rows)) || [];
      this.setState({ health, kpi, token, fleetRows, loading: false });
    } catch (e) {
      this.setState({ error: e.message || "Failed to load dashboard", loading: false });
    }
  };

  ratingHistoryTitle(row) {
    const history = (row.rating_history || [])
      .filter((entry) => entry.rank != null)
      .map((entry) => `${entry.date}: #${entry.rank}`)
      .join("\n");
    return history
      ? `Rating history\n30d average: ${row.rating_rank_30d == null ? "—" : `#${row.rating_rank_30d}`}\n5d average: ${row.rating_rank_5d == null ? "—" : `#${row.rating_rank_5d}`}\nTop 5: ${row.rating_top5_days_30d || 0}/${row.rating_observed_days_30d || 0} rated days\n\n${history}`
      : "No rated history yet. A strategy needs 10 closed shadow trades.";
  }

  render() {
    const { loading, error, health, kpi, token, fleetRows } = this.state;
    const activeLive = (fleetRows || []).filter((r) => r && r.live_open);
    return (
      <TradesSiteWrapper>
        <Page.Content
          title="Dashboard"
          subTitle="Status and KPIs only — other sections load on their own pages."
          options={<RefreshButton onClick={this.load} loading={loading} />}
        >
          <Alert type="info">
            <span data-page-note="1">
              If token validation says FAIL, use Update token before the session. This page does not place orders.
            </span>
          </Alert>
          {error ? <Alert type="danger">{error}</Alert> : null}
          {!loading && token && token.plan_required ? (
            <Alert type="warning">
              <strong>No valid plan.</strong> Purchase a plan to use live strategies.
              {" "}
              <a href="/account#package" className="btn btn-sm btn-primary ml-2">
                Purchase a plan
              </a>
            </Alert>
          ) : null}
          {loading ? <Text>Loading dashboard…</Text> : null}
          {!loading && !error ? (
            <React.Fragment>
              <Grid.Row cards={true}>
                <Grid.Col sm={6} lg={3}>
                  <StatsCard
                    layout={1}
                    total={kpi ? fmtCurrency(kpi.pnl_today) : "--"}
                    label="P&L Today"
                  />
                </Grid.Col>
                <Grid.Col sm={6} lg={3}>
                  <StatsCard layout={1} total={kpi ? `${kpi.win_rate_today.toFixed(1)}%` : "--"} label="Win rate today" />
                </Grid.Col>
                <Grid.Col sm={6} lg={3}>
                  <StatsCard layout={1} total={kpi ? String(kpi.trades_today) : "--"} label="Trades today" />
                </Grid.Col>
                <Grid.Col sm={6} lg={3}>
                  <StatsCard
                    layout={1}
                    total={token && token.live_token_ok ? "PASS" : "FAIL"}
                    label="Token validation"
                  />
                </Grid.Col>
              </Grid.Row>
              {activeLive.length > 0 ? (
                <Grid.Row>
                  <Grid.Col>
                    <Card title="Active live strategies">
                      <Card.Body className="p-0">
                        {activeLive.map((r) => (
                          <div
                            key={r.strategy_id}
                            style={{
                              backgroundColor: "#d4edda",
                              padding: "10px 16px",
                              borderBottom: "1px solid #c3e6cb",
                            }}
                          >
                            <strong>{r.display_name || r.strategy_id}</strong>
                            <span className="text-muted small ml-2">{r.strategy_id}</span>
                            <span className={`badge ml-2 ${r.rating_status === "live_candidate" ? "badge-success" : "badge-warning"}`}>
                              {r.rating == null ? "Rating pending" : `Rating ${Number(r.rating).toFixed(1)}/100`}
                            </span>
                            <span
                              title={this.ratingHistoryTitle(r)}
                              aria-label="Show rating history"
                              role="img"
                              className="ml-1 text-muted"
                              style={{ cursor: "help" }}
                            >
                              ⓘ
                            </span>
                            {r.live_qty ? (
                              <span className="badge badge-success ml-2">qty {r.live_qty}</span>
                            ) : null}
                          </div>
                        ))}
                      </Card.Body>
                    </Card>
                  </Grid.Col>
                </Grid.Row>
              ) : null}
              <Grid.Row>
                <Grid.Col lg={6}>
                  <Card title="Service">
                    <Card.Body>
                      <p><strong>Healthy:</strong> {health && health.ok ? "Yes" : "No"}</p>
                      <p><strong>Version:</strong> {health && health.version ? health.version : "-"}</p>
                      <p><strong>Time (IST):</strong> {health && health.time_ist ? fmtDateTime(health.time_ist) : "-"}</p>
                      {kpi && kpi.fleet_mode ? (
                        <React.Fragment>
                          <p><strong>Fleet live armed:</strong> {kpi.fleet_live_armed_count || 0}</p>
                          {(kpi.fleet_live_armed_ids || []).length > 0 ? (
                            <p className="text-muted small">{(kpi.fleet_live_armed_ids || []).join(", ")}</p>
                          ) : (
                            <p className="text-muted small">None — enable on Strategy Fleet</p>
                          )}
                        </React.Fragment>
                      ) : (
                        <p><strong>Trading enabled:</strong> {kpi && kpi.trading_enabled ? "Yes" : "No"}</p>
                      )}
                    </Card.Body>
                  </Card>
                </Grid.Col>
                <Grid.Col lg={6}>
                  <Card title="Trading account">
                    <Card.Body>
                      {token && token.profile_summary ? (
                        <React.Fragment>
                          <p><strong>User:</strong> {token.profile_summary.user_id || "-"}</p>
                          <p><strong>Name:</strong> {token.profile_summary.user_name || "-"}</p>
                          <p><strong>Token today:</strong> {token.has_token ? "Yes" : "No"}</p>
                          <p className={token.live_token_ok ? "text-success" : "text-danger"}>
                            Live validation: {token.live_token_ok ? "PASS" : "FAIL"}
                          </p>
                        </React.Fragment>
                      ) : (
                        <p className="text-muted">No profile loaded.</p>
                      )}
                    </Card.Body>
                  </Card>
                </Grid.Col>
              </Grid.Row>
              <Grid.Row>
                <Grid.Col>
                  <Card title="All-time summary">
                    <Card.Body>
                      <p className={kpi ? pnlClass(kpi.pnl_all_time) : ""}>
                        <strong>P&L all time:</strong> {kpi ? fmtCurrency(kpi.pnl_all_time) : "--"}
                      </p>
                      <p><strong>Win rate all time:</strong> {kpi ? `${kpi.win_rate_all_time.toFixed(1)}%` : "--"}</p>
                      <p><strong>Profit factor:</strong> {kpi && kpi.profit_factor_all_time != null ? kpi.profit_factor_all_time.toFixed(2) : "--"}</p>
                    </Card.Body>
                  </Card>
                </Grid.Col>
              </Grid.Row>
              <Card title="How this app trades your account" className="mb-3">
                <Card.Body>
                  <span data-trade-flow="1" />
                  <ol className="mb-0">
                    <li>
                      <strong>Sign in to this app.</strong> This login is only for this app. It is not your broker password.
                    </li>
                    <li>
                      <strong>Link your trading account for today.</strong> Click Update token and finish the broker login.
                      That stores a token on your account. Orders later today go to the trading account you just logged into.
                      Yesterday&apos;s token cannot trade.
                    </li>
                    <li>
                      <strong>Strategies decide. Shadow does not send orders.</strong> The app watches NIFTY and SENSEX and
                      chooses entries and exits. Until we turn a strategy on, it only records a paper trade.
                      Paper trades are not sent to your broker.
                    </li>
                    <li>
                      <strong>We turn a strategy on after you purchase.</strong> You cannot turn a strategy on yourself.
                      A strategy we turn on sends orders to your trading account and uses your funds. Turning it off
                      stops new orders for that strategy. The paper book keeps running.
                    </li>
                    <li>
                      <strong>Check the result on your book only.</strong> Trades shows what the broker filled. Decisions shows
                      why. Logs shows your own errors. Another trader&apos;s book is not shown.
                    </li>
                  </ol>
                </Card.Body>
              </Card>
            </React.Fragment>
          ) : null}
        </Page.Content>
      </TradesSiteWrapper>
    );
  }
}

export default DashboardPage;
